import React, { useEffect, useRef } from 'react';

import styled from 'styled-components';

import { GetWeatherDataApiResult } from 'api/skyw/getWeatherDataApi';

import { convertZeroNumber } from 'libs/utils';

const TOTAL_HOUR = 12;
const Y_LABEL_FONT = "bold 14px 'Montserrat', sans-serif";
const X_LABEL_TIME_FONT = "normal 14px 'Montserrat', sans-serif";
const X_LABEL_DATE_FONT = "bold 14px 'Montserrat', sans-serif";
const T_RANGE_LIST = [-50, -40, -30, -20, -10, 0, 10];
const C_RANGE_LIST = [-120, -110, -100, -90, -80, -70, -60, -50, -40];
const W_RANGE_LIST = [10, 15, 20, 25, 30];
const Y_LABEL_LIST = [
  [-20, 40],
  [0, 100],
  [-40, 0],
  [0, 10],
];
const SECTION_PADDING_Y = 2;

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-top: 80%;
`;

const Canvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  aspect-ratio: auto 915 / 735;
  overflow: clip;
  overflow-clip-margin: content-box;
`;

const CntCanvas = styled(Canvas)`
  animation: fade 2.8s ease-in-out 2s infinite;

  @keyframes fade {
    0% {
      opacity: 1;
    }
    30% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    80% {
      opacity: 1;
    }
    100% {
      opacity: 1;
    }
  }
`;

interface WeatherGraphProps {
  data?: GetWeatherDataApiResult;
}

const WeatherGraph = ({ data }: WeatherGraphProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cntCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !cntCanvasRef.current || !data) return;
    const canvas = canvasRef.current;
    const cntCanvas = cntCanvasRef.current;
    const ctx = canvas.getContext('2d')!;
    const cntCtx = cntCanvas.getContext('2d')!;

    const yLabelList = [...Y_LABEL_LIST];

    const getGraph = () => {
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const yLabelWidth = canvasWidth / (TOTAL_HOUR + 1);
      const xLabelHeight = yLabelWidth;
      const paddingTop = xLabelHeight / 2;
      const paddingRight = yLabelWidth / 2;

      return {
        width: canvasWidth - yLabelWidth - paddingRight,
        height: canvasHeight - xLabelHeight - paddingTop,
        start: {
          x: yLabelWidth,
          y: paddingTop,
        },
        end: {
          x: canvasWidth - paddingRight,
          y: canvasHeight - xLabelHeight,
        },
      };
    };

    const getSection = () => {
      const graph = getGraph();
      return {
        graph: graph,
        sectionHeight: graph.height / 4,
        section: {
          t: {
            start: {
              x: graph.start.x,
              y: graph.start.y,
            },
            end: {
              x: graph.end.x,
              y: graph.start.y + (graph.height / 4) * 0,
            },
            isGap: false,
            divid: 3,
            unit: ' °C',
            icon: {
              path: '/assets/skyw/t_icon.png',
              offset: [-3, -23],
              size: 21,
            },
          },
          h: {
            start: {
              x: graph.start.x,
              y: graph.start.y + (graph.height / 4) * 1,
            },
            end: {
              x: graph.end.x,
              y: graph.start.y + (graph.height / 4) * 1,
            },
            isGap: true,
            divid: 10,
            unit: ' %',
            icon: {
              path: '/assets/skyw/h_icon.png',
              offset: [-7, -24],
              size: 30,
            },
          },
          c: {
            start: {
              x: graph.start.x,
              y: graph.start.y + (graph.height / 4) * 2,
            },
            end: {
              x: graph.end.x,
              y: graph.start.y + (graph.height / 4) * 2,
            },
            isGap: true,
            divid: 10,
            unit: '',
            icon: {
              path: '/assets/skyw/c_icon.png',
              offset: [-15, -17],
              size: 50,
            },
          },
          w: {
            start: {
              x: graph.start.x,
              y: graph.start.y + (graph.height / 4) * 3,
            },
            end: {
              x: graph.end.x,
              y: graph.start.y + (graph.height / 4) * 3,
            },
            isGap: true,
            divid: 3,
            unit: ' m/s',
            icon: {
              path: '/assets/skyw/w_icon.png',
              offset: [-10, -16],
              size: 44,
            },
          },
        },
      };
    };

    const drawGraph = () => {
      const { section, sectionHeight, graph } = getSection();
      const sections = Object.values(section);
      sections.forEach((section) => {
        ctx.beginPath();
        ctx.strokeStyle = '#686B6B';
        ctx.lineWidth = 2;
        ctx.moveTo(section.start.x, section.start.y);
        ctx.lineTo(section.end.x, section.end.y);
        ctx.stroke();
        ctx.closePath();

        const img = new Image();
        img.src = section.icon.path;
        img.onload = () => {
          ctx.drawImage(
            img,
            section.end.x + section.icon.offset[0],
            section.end.y + section.icon.offset[1],
            section.icon.size,
            (section.icon.size * img.height) / img.width
          );
        };

        ctx.beginPath();
        ctx.lineWidth = 0.5;
        ctx.strokeStyle = '#353A3A';
        const gapY = section.isGap ? sectionHeight / 11 : 0;
        const newSectionHeight = sectionHeight - gapY;

        if (section.isGap) {
          ctx.moveTo(section.start.x, section.start.y + gapY);
          ctx.lineTo(section.end.x, section.start.y + gapY);
          ctx.stroke();
        }

        for (let i = 1; i < section.divid; i++) {
          const gapHeight = newSectionHeight / section.divid;
          ctx.moveTo(section.start.x, section.start.y + gapY + gapHeight * i);
          ctx.lineTo(section.end.x, section.start.y + gapY + gapHeight * i);
          ctx.stroke();
        }
        ctx.closePath();
      });

      ctx.beginPath();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#686B6B';
      ctx.moveTo(graph.start.x, graph.end.y);
      ctx.lineTo(graph.end.x, graph.end.y);
      ctx.stroke();
      ctx.closePath();
    };

    const drawXLabel = () => {
      const graph = getGraph();
      const gapWidth = graph.width / TOTAL_HOUR;
      const cntDate = new Date(`${data.min_date_time.date.replaceAll('-', '/')} ${data.min_date_time.time}`);
      for (let i = 0; i < TOTAL_HOUR + 1; i++) {
        const hours = cntDate.getHours();
        const time = `${convertZeroNumber(hours)}:00`;
        const date = `${convertZeroNumber(cntDate.getMonth() + 1)}.${convertZeroNumber(cntDate.getDate())}.`;

        const pointPos = {
          x: graph.start.x + gapWidth * i,
          y: graph.end.y,
        };
        const textPos = {
          x: pointPos.x,
          y: pointPos.y + 24,
        };

        if (i < TOTAL_HOUR && (hours >= 18 || hours <= 3)) {
          ctx.beginPath();
          ctx.fillStyle = 'rgba(24, 24, 24, 0.6)';
          ctx.fillRect(graph.start.x + gapWidth * i, graph.start.y, gapWidth, graph.height);
          ctx.closePath();
        }

        ctx.beginPath();
        ctx.lineWidth = 0.5;
        ctx.strokeStyle = i === 0 || i === TOTAL_HOUR ? '#1f1f1f' : '#353A3A';
        ctx.moveTo(pointPos.x, graph.start.y);
        ctx.lineTo(pointPos.x, pointPos.y);
        ctx.stroke();
        ctx.closePath();

        ctx.beginPath();
        ctx.fillStyle = '#686B6B';
        ctx.arc(pointPos.x, pointPos.y, 4, 0, 2 * Math.PI);
        ctx.fill();
        ctx.closePath();

        ctx.save();
        ctx.font = X_LABEL_TIME_FONT;
        ctx.fillStyle = '#ADADAE';
        ctx.textAlign = 'center';
        ctx.translate(textPos.x, textPos.y);
        ctx.rotate(-Math.PI / 10);
        ctx.translate(-textPos.x, -textPos.y);
        ctx.fillText(time, textPos.x, textPos.y);
        ctx.restore();

        if (time === '00:00') {
          ctx.save();
          ctx.font = X_LABEL_DATE_FONT;
          ctx.fillStyle = '#ADADAE';
          ctx.textAlign = 'center';
          ctx.translate(textPos.x + 4, textPos.y + 12);
          ctx.rotate(-Math.PI / 10);
          ctx.translate(-(textPos.x + 4), -(textPos.y + 12));
          ctx.fillText(date, textPos.x + 4, textPos.y + 12);
          ctx.restore();
        }

        cntDate.setHours(cntDate.getHours() + 3);
      }
    };

    const drawYLabel = () => {
      const { section, sectionHeight } = getSection();
      const interval = 30;

      const tDiffMinMax = data.min_max_mean_list[0][1] - data.min_max_mean_list[0][0];
      if (tDiffMinMax < interval && !isNaN(data.min_max_mean_list[0][2])) {
        const tRangeMean0 = T_RANGE_LIST[0] + interval / 2;
        const tMean = data.min_max_mean_list[0][2];
        const rangeDiff = T_RANGE_LIST[1] - T_RANGE_LIST[0];
        const result = (tMean - tRangeMean0) / rangeDiff;
        const lastIdx = T_RANGE_LIST.length - 1;
        const initIdx = Math.floor(result * 2) - Math.floor(result);
        const idx = initIdx < 0 ? 0 : initIdx > lastIdx ? lastIdx : initIdx;

        yLabelList[0] = [T_RANGE_LIST[idx], T_RANGE_LIST[idx] + interval];
      }

      if (!isNaN(data.min_max_mean_list[2][2])) {
        const cMaxDefault = yLabelList[2][1];
        const cMin = data.min_max_mean_list[2][0];
        const result = cMin - C_RANGE_LIST[0];
        const rangeDiff = C_RANGE_LIST[1] - C_RANGE_LIST[0];
        const initIdx = Math.floor(result / rangeDiff);
        const lastIdx = C_RANGE_LIST.length - 1;
        const idx = initIdx < 0 ? 0 : initIdx > lastIdx ? lastIdx : initIdx;

        yLabelList[2] = [C_RANGE_LIST[idx], cMaxDefault];
      }

      if (!isNaN(data.min_max_mean_list[3][2])) {
        const wMinDefault = yLabelList[3][0];
        const wMax = data.min_max_mean_list[3][1];
        const result = wMax - W_RANGE_LIST[0];
        const rangeDiff = W_RANGE_LIST[1] - W_RANGE_LIST[0];
        const initIdx = Math.floor(result / rangeDiff + 1);
        const lastIdx = W_RANGE_LIST.length - 1;
        const idx = initIdx < 0 ? 0 : initIdx > lastIdx ? lastIdx : initIdx;

        yLabelList[3] = [wMinDefault, W_RANGE_LIST[idx]];
      }

      const sections = Object.values(section);

      sections.forEach((section, i) => {
        const gapY = section.isGap ? sectionHeight / 11 : 0;
        const newSectionHeight = sectionHeight - gapY;

        const startPos = {
          x: section.start.x - 8,
          y: section.start.y + gapY + 5,
        };
        const endPos = {
          x: section.end.x,
          y: startPos.y + newSectionHeight,
        };

        ctx.save();
        ctx.font = Y_LABEL_FONT;
        ctx.fillStyle = '#ADADAE';
        ctx.textAlign = 'right';
        ctx.fillText(`${yLabelList[i][1]}${section.unit}`, startPos.x, startPos.y);
        ctx.restore();

        ctx.save();
        ctx.font = Y_LABEL_FONT;
        ctx.fillStyle = '#ADADAE';
        ctx.textAlign = 'right';
        ctx.fillText(`${yLabelList[i][0]}${section.unit}`, startPos.x, endPos.y);
        ctx.restore();

        if (section.divid === 3) {
          const sectionGapY = newSectionHeight / 3;
          const valueGap = (yLabelList[i][1] - yLabelList[i][0]) / 3;

          for (let j = 1; j < 3; j++) {
            ctx.save();
            ctx.font = Y_LABEL_FONT;
            ctx.fillStyle = '#ADADAE';
            ctx.textAlign = 'right';
            ctx.fillText(`${Math.floor(yLabelList[i][1] - valueGap * j)}`, startPos.x, startPos.y + sectionGapY * j);
            ctx.restore();
          }
        } else {
          const sectionGapY = newSectionHeight / 2;
          const valueGap = (yLabelList[i][1] - yLabelList[i][0]) / 2;

          ctx.save();
          ctx.font = Y_LABEL_FONT;
          ctx.fillStyle = '#ADADAE';
          ctx.textAlign = 'right';
          ctx.fillText(`${Math.floor(yLabelList[i][1] - valueGap)}`, startPos.x, startPos.y + sectionGapY);
          ctx.restore();
        }
      });
    };

    const drawData = () => {
      const { section, graph, sectionHeight } = getSection();
      const gapWidth = graph.width / TOTAL_HOUR;
      const minutesWidth = gapWidth / 3 / 60; // 갭 하나에 3시간, 60분으로 나눠서 분당 넓이
      const minDate = new Date(`${data.min_date_time.date.replaceAll('-', '/')} ${data.min_date_time.time}`);

      const sections = Object.values(section);

      data.time_list.forEach((time, i) => {
        const fileDate = new Date(time.replaceAll('-', '/'));
        const diffHours = Math.floor((fileDate.getTime() - minDate.getTime()) / 1000 / 60 / 60);
        const idx = Math.floor(diffHours / 3);
        const minutes = (fileDate.getHours() % 3) * 60 + fileDate.getMinutes();
        const weatherDataList = [data.t_list[i], data.h_list[i], data.c_list[i], data.w_list[i]];
        const x = section.t.start.x + gapWidth * idx + minutes * minutesWidth;

        const isLastTime = i === data.time_list.length - 1;
        if (isLastTime) {
          ctx.beginPath();
          ctx.strokeStyle = '#0089F9';
          ctx.lineWidth = 0.5;
          ctx.moveTo(x, graph.start.y);
          ctx.lineTo(x, graph.end.y);
          ctx.stroke();
          ctx.closePath();
        }

        sections.forEach((section, j) => {
          const weatherData = weatherDataList[j];

          if (weatherData[0] !== 'N/A') {
            const min = yLabelList[j][0];
            const max = yLabelList[j][1];
            const yGap = section.isGap ? sectionHeight / 11 : 0;
            const valueHeight = (sectionHeight - yGap - SECTION_PADDING_Y) / (max - min);
            const value = weatherData[0] < min ? min : weatherData[0] > max ? max : weatherData[0];
            const y = section.start.y + sectionHeight - (value - min) * valueHeight - SECTION_PADDING_Y;

            const isBadStatus = weatherData[1] === 'N';

            ctx.fillStyle = !isBadStatus ? '#88A6C1' : '#CF4A40';
            if (j === 0 && weatherData[0] <= 0) {
              ctx.fillStyle = '#4088EE';
              if (isBadStatus) {
                ctx.fillStyle = "#CF4A40";
              }
            }
            ctx.fillRect(x, y, 1, 1);

            if (isLastTime) {
              const img = new Image();
              img.src = !isBadStatus ? '/assets/skyw/dot_ani.png' : '/assets/skyw/dot_ani_1.png';
              img.onload = () => {
                const offset = img.width / 2;
                cntCtx.drawImage(img, x - offset, y - offset);
              };
            }
          }
        });
      });
    };

    const initCanvas = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      cntCtx.clearRect(0, 0, cntCanvas.width, cntCanvas.height);
    };

    const draw = () => {
      initCanvas();
      drawYLabel();
      drawXLabel();
      drawGraph();
      drawData();
    };

    draw();
  }, [canvasRef, cntCanvasRef, data]);
  return (
    <Container>
      <Canvas ref={canvasRef} width="915px" height="745px"></Canvas>
      <CntCanvas ref={cntCanvasRef} width="915px" height="745px"></CntCanvas>
    </Container>
  );
};

export default React.memo(WeatherGraph);
